import { graphql, readInlineData } from 'react-relay/legacy';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import get from 'lodash.get';

const getCheckoutChargesCurrencyDisclaimerTransactionFragment = graphql`
    fragment getCheckoutChargesCurrencyDisclaimer_transaction on Transaction @inline {
        buyerChargeCurrency
        buyer {
            hasSupportedChargeCurrency
            preferences {
                currency
            }
        }
        buyerInvoice {
            totalAmount {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        summary {
            checkoutPaymentAuthorization {
                authorizationAmount {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
        }
        uiMessages {
            foreignExchangeDisclaimer
        }
    }
`;

const getCheckoutChargesCurrencyDisclaimerViewerFragment = graphql`
    fragment getCheckoutChargesCurrencyDisclaimer_viewer on Viewer @inline {
        chargeCurrencies
    }
`;

const getTransactionAmount = (totals, currency) =>
    (get(totals, 'convertedAmountList') || []).find(total => total.currency === currency);

export function getCheckoutChargesCurrencyDisclaimer(viewerRef, transactionRef, isConfirmation) {
    const transaction = readInlineData(
        getCheckoutChargesCurrencyDisclaimerTransactionFragment,
        transactionRef
    );
    const viewer = readInlineData(getCheckoutChargesCurrencyDisclaimerViewerFragment, viewerRef);
    const localizationSupported = get(transaction, 'buyer.hasSupportedChargeCurrency');
    const buyerCurrencyPreferenceNonUSD = get(transaction, 'buyer.preferences.currency') !== 'USD';
    const isNonUSDSupportedLocalization = localizationSupported && buyerCurrencyPreferenceNonUSD;
    const { buyerChargeCurrency } = transaction;
    const { chargeCurrencies = [] } = viewer;
    const totalAmount = get(
        transaction,
        isConfirmation
            ? 'summary.checkoutPaymentAuthorization.authorizationAmount'
            : 'buyerInvoice.totalAmount'
    );

    if (isConfirmation) {
        const fxDisclaimer = get(transaction, 'uiMessages.foreignExchangeDisclaimer');
        if (fxDisclaimer) {
            return fxDisclaimer;
        }
    } else if (!localizationSupported) {
        const currency = chargeCurrencies.includes(buyerChargeCurrency)
            ? buyerChargeCurrency
            : 'USD';
        const amount = (
            <FormattedNumber
                value={get(getTransactionAmount(totalAmount, currency), 'amount')}
                currency={currency}
                currencyDisplay="symbol"
                style="currency"
            />
        );
        return (
            <FormattedMessage
                id="checkout.CheckoutChargesCurrencyDisclaimer.unsupportedCurrencyAndForeignFee"
                defaultMessage="You will be charged in {currency} and a foreign transaction fee may apply. Your current order total is {amount}."
                values={{
                    currency: <span data-tn="discalimer-buyer-charge-currency">{currency}</span>,
                    amount: <span data-tn="discalimer-buyer-charge-currency-amount">{amount}</span>,
                }}
            />
        );
    } else if (isNonUSDSupportedLocalization) {
        return (
            <FormattedMessage
                id="checkout.CheckoutChargesCurrencyDisclaimer.foreignFee"
                defaultMessage="A foreign transaction fee may apply."
            />
        );
    }
    return null;
}
